// src/views/Users/Customers/Customers.jsx
import { useState, useEffect, useRef } from "react";
import { employeeConstants, modeConstants } from "../../../constants";
import { useDispatch, useSelector } from "react-redux";
import ActionsDataTable from "../../../shared/ActionsDataTable";
import { employeeActions, userActions } from "../../../redux/actions";
import { getUserData, SCREENS_IDS } from "../../../system";
import { success } from "../../../redux/actions/utilities";
import Timer from "../../../shared/Timer";
import ViewAttendanceSessionsModal from "../../../shared/ViewAttendanceSessionsModal";

export default function EmployeeAttendance() {
  const initialGridState = {
    column: "",
    type: "",
    search: null,
    filters: [],
  };

  const dispatch = useDispatch();
  const sortingInProgressRef = useRef(false);
  const decryptedData = getUserData();
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState(null);
  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(initialGridState.pageNumber);
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState(modeConstants.ADD_MODE);
  // modal + row state
  const [rowData, setRowData] = useState([]);
  const [viewModal, setShowViewModal] = useState(false);
  const [filterType, setFilterType] = useState("all");

  const allData = useSelector((s) => s.employeeReducer.employeeAttendance);
  // filters aligned to table fields
  const filterColumns = [];

  const columns = [
    {
      name: "Sr. No",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "100px",
    },
    {
      id: "date",
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      cell: (row) => {
        const date = row.date ? new Date(row.date).toLocaleDateString() : "-";
        return (
          <div className="d-flex align-items-center">
            <span>{date}</span>
          </div>
        );
      },
    },
    {
      id: "total_duration",
      name: "Total Hours",
      selector: (row) => row.total_duration,
      sortable: true,
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span>{row.total_duration}</span>
        </div>
      ),
    },
    {
      name: "Actions",
      sortable: false,
      width: "170px",
      cell: (row) => (
        <div
          className="d-flex align-items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            title="Edit"
            aria-label="Edit"
            onClick={() => {
              getRowData(row);
              setShowViewModal(true);
            }}
          >
            <i className="bi bi-eye" />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const getRowData = (row) => {
    const sessionsWithDate = (row.sessions || []).map((s) => ({
      ...s,
      parent_date: row.date,
    }));
    dispatch(success(sessionsWithDate, employeeConstants.EMPLOYEE_SESSIONS));
  };
  const onSort = (sortedColumn, sortDirection) => {
    sortingInProgressRef.current = true;
    const columnDirection = sortDirection ? sortDirection.toUpperCase() : null;
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null ? `${sortedColumn.id} ${columnDirection}` : "",
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  const getAll = () => {
      dispatch(employeeActions.getAllEmployeeAttendanceById(decryptedData.id));
  };

  // Daily, weekly, monthly date filters helper
  const filterData = (data, type) => {
    if (!data) return [];
    if (type === "all") return data;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return data.filter((row) => {
      if (!row.date) return false;
      const rowDate = new Date(row.date);
      rowDate.setHours(0, 0, 0, 0);

      if (type === "daily") {
        return rowDate.getTime() === today.getTime();
      }

      if (type === "weekly") {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - 7);
        return rowDate >= startOfWeek && rowDate <= today;
      }

      if (type === "monthly") {
        const startOfMonth = new Date(today);
        startOfMonth.setDate(today.getDate() - 30);
        return rowDate >= startOfMonth && rowDate <= today;
      }

      return true;
    });
  };

  useEffect(() => {
    if (allData !== null) {
      setRowData(filterData(allData, filterType));
    }
  }, [allData, filterType]);

  useEffect(() => {
    getAll();
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    getAll();
    // eslint-disable-next-line
  }, []);

  const handleCheckIn = async () => {
    dispatch(employeeActions.employeeCheckIn(decryptedData.id));
    setIsCheckedIn(true);
    setCheckInTime(new Date().toISOString());
  };

  const handleCheckOut = async () => {
    dispatch(employeeActions.employeeCheckOut(decryptedData.id));
    setIsCheckedIn(false);
    setCheckInTime(null);
  };

  return (
    <>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            {/* Buttons area */}
            <button
              type="button"
              className="btn btn-success me-2"
              onClick={handleCheckIn}
              title="Check In"
            >
              Check In
            </button>

            <button
              type="button"
              className="btn btn-primary me-3"
              onClick={handleCheckOut}
              title="Check Out"
            >
              Check Out
            </button>

            <select
              className="form-select form-select-sm"
              style={{ width: "135px", border: "1px solid #ced4da", borderRadius: 8, fontSize: 13, height: 38 }}
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Dates</option>
              <option value="daily">Today (Daily)</option>
              <option value="weekly">Last 7 Days</option>
              <option value="monthly">Last 30 Days</option>
            </select>
          </div>
          <Timer isRunning={isCheckedIn} startTime={checkInTime} />
        </div>

      <ViewAttendanceSessionsModal
        show={viewModal}
        onHide={() => setShowViewModal(false)}
      />
      {/* optional place for Add button or filters if needed */}
      <ActionsDataTable
        columns={columns}
        filterColumns={filterColumns}
        data={rowData}
        pagination
        highlightOnHover
        striped
        responsive
        persistTableHead
        selectableRows
        selectableRowsHighlight
        selectableRowsVisibleOnly
        paginationServer={false}
        onSort={onSort}
        gridState={gridState}
        paginationTotalRows={rowData?.length}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        paginationDefaultPage={defaultPage}
        paginationResetDefaultPage
        setGridState={setGridState}
        setShowModal={setShowModal}
        setMode={setMode}
        allowToolbar={false}
        allowFilters={false}
      />
    </>
  );
}
